export type OperatorBookingPageTheme = {
  flowStyle: 'menu_first' | 'address_first'
  pageLayout: 'standard' | 'showcase' | 'split'
  accentColor: string
  fontStyle: 'classic' | 'modern' | 'editorial'
  surfaceStyle: 'classic' | 'modern' | 'industrial' | 'editorial'
  serviceCardStyle: 'balanced' | 'editorial' | 'compact'
  showDuration: boolean
  hero: {
    bannerImageUrl: string
    backgroundImageUrl: string
    headline: string
    subheadline: string
    overlayStyle: 'none' | 'soft' | 'strong'
  }
  bookingCopy: {
    intro: string
    serviceMenuTitle: string
    accessInstructions: string
    weatherPolicy: string
    cancellationPolicy: string
  }
  confirmation: {
    headline: string
    body: string
  }
  completion: {
    layout: 'simple' | 'branded'
    primaryCta: 'manage' | 'rebook' | 'contact' | 'none'
    secondaryCta: 'manage' | 'rebook' | 'contact' | 'none'
    showPrepChecklist: boolean
    showContactCard: boolean
    showRebookCta: boolean
  }
}

export type CatalogBookingPageConfig = Record<string, never>

export type DisplayGroupBookingPageConfig = {
  priceDisplay: 'standard' | 'from' | 'hidden'
}

export type ServicePriceDisplay = DisplayGroupBookingPageConfig['priceDisplay']

export type ServiceBookingPageConfig = {
  badgeLabel: string
  priceDisplay: 'inherit' | 'standard' | 'from' | 'hidden'
  prepChecklist: string[]
  imageUrl: string
  includedItems: string[]
  featuredLabel: string
  upsellNote: string
}

export type ConfirmationTemplateContext = Partial<{
  businessName: string
  customerName: string
  serviceName: string
  serviceList: string
  date: string
  startTime: string
  endTime: string
  address: string
  bookingReference: string
}>

export const CONFIRMATION_TEMPLATE_TOKENS = [
  'businessName',
  'customerName',
  'serviceName',
  'serviceList',
  'date',
  'startTime',
  'endTime',
  'address',
  'bookingReference',
] as const

export const DEFAULT_OPERATOR_BOOKING_THEME: OperatorBookingPageTheme = {
  flowStyle: 'menu_first',
  pageLayout: 'standard',
  accentColor: '#9D654F',
  fontStyle: 'classic',
  surfaceStyle: 'classic',
  serviceCardStyle: 'balanced',
  showDuration: true,
  hero: {
    bannerImageUrl: '',
    backgroundImageUrl: '',
    headline: '',
    subheadline: '',
    overlayStyle: 'soft',
  },
  bookingCopy: {
    intro: '',
    serviceMenuTitle: 'Services',
    accessInstructions: '',
    weatherPolicy: '',
    cancellationPolicy: '',
  },
  confirmation: {
    headline: '',
    body: '',
  },
  completion: {
    layout: 'simple',
    primaryCta: 'manage',
    secondaryCta: 'none',
    showPrepChecklist: true,
    showContactCard: true,
    showRebookCta: false,
  },
}

export const DEFAULT_CATALOG_BOOKING_CONFIG: CatalogBookingPageConfig = {}

export const DEFAULT_DISPLAY_GROUP_BOOKING_CONFIG: DisplayGroupBookingPageConfig = {
  priceDisplay: 'standard',
}

export const DEFAULT_SERVICE_BOOKING_CONFIG: ServiceBookingPageConfig = {
  badgeLabel: '',
  priceDisplay: 'inherit',
  prepChecklist: [],
  imageUrl: '',
  includedItems: [],
  featuredLabel: '',
  upsellNote: '',
}

export function normalizeOperatorBookingTheme(
  theme: Partial<OperatorBookingPageTheme> | null | undefined
): OperatorBookingPageTheme {
  const hero = theme?.hero
  const completion = theme?.completion

  return {
    flowStyle: normalizeFlowStyle(theme?.flowStyle),
    pageLayout: normalizePageLayout(theme?.pageLayout),
    accentColor: isHexColor(theme?.accentColor)
      ? theme.accentColor
      : DEFAULT_OPERATOR_BOOKING_THEME.accentColor,
    fontStyle:
      theme?.fontStyle === 'modern' || theme?.fontStyle === 'editorial'
        ? theme.fontStyle
        : DEFAULT_OPERATOR_BOOKING_THEME.fontStyle,
    surfaceStyle: normalizeSurfaceStyle(theme?.surfaceStyle),
    serviceCardStyle:
      theme?.serviceCardStyle === 'editorial' || theme?.serviceCardStyle === 'compact'
        ? theme.serviceCardStyle
        : DEFAULT_OPERATOR_BOOKING_THEME.serviceCardStyle,
    showDuration:
      typeof theme?.showDuration === 'boolean'
        ? theme.showDuration
        : DEFAULT_OPERATOR_BOOKING_THEME.showDuration,
    hero: {
      bannerImageUrl: normalizeText(hero?.bannerImageUrl).slice(0, 2048),
      backgroundImageUrl: normalizeText(hero?.backgroundImageUrl).slice(0, 2048),
      headline: normalizeText(hero?.headline).slice(0, 120),
      subheadline: normalizeText(hero?.subheadline).slice(0, 240),
      overlayStyle:
        hero?.overlayStyle === 'none' || hero?.overlayStyle === 'strong'
          ? hero.overlayStyle
          : DEFAULT_OPERATOR_BOOKING_THEME.hero.overlayStyle,
    },
    bookingCopy: {
      intro: normalizeText(theme?.bookingCopy?.intro),
      serviceMenuTitle:
        normalizeText(theme?.bookingCopy?.serviceMenuTitle).slice(0, 96) ||
        DEFAULT_OPERATOR_BOOKING_THEME.bookingCopy.serviceMenuTitle,
      accessInstructions: normalizeText(theme?.bookingCopy?.accessInstructions),
      weatherPolicy: normalizeText(theme?.bookingCopy?.weatherPolicy),
      cancellationPolicy: normalizeText(theme?.bookingCopy?.cancellationPolicy),
    },
    confirmation: {
      headline: normalizeText(theme?.confirmation?.headline),
      body: normalizeText(theme?.confirmation?.body),
    },
    completion: {
      layout:
        completion?.layout === 'branded'
          ? 'branded'
          : DEFAULT_OPERATOR_BOOKING_THEME.completion.layout,
      primaryCta: normalizeCompletionCta(
        completion?.primaryCta,
        DEFAULT_OPERATOR_BOOKING_THEME.completion.primaryCta
      ),
      secondaryCta: normalizeCompletionCta(
        completion?.secondaryCta,
        DEFAULT_OPERATOR_BOOKING_THEME.completion.secondaryCta
      ),
      showPrepChecklist:
        typeof completion?.showPrepChecklist === 'boolean'
          ? completion.showPrepChecklist
          : DEFAULT_OPERATOR_BOOKING_THEME.completion.showPrepChecklist,
      showContactCard:
        typeof completion?.showContactCard === 'boolean'
          ? completion.showContactCard
          : DEFAULT_OPERATOR_BOOKING_THEME.completion.showContactCard,
      showRebookCta:
        typeof completion?.showRebookCta === 'boolean'
          ? completion.showRebookCta
          : DEFAULT_OPERATOR_BOOKING_THEME.completion.showRebookCta,
    },
  }
}

export function normalizeCatalogBookingConfig(
  _config: Partial<CatalogBookingPageConfig> | null | undefined
): CatalogBookingPageConfig {
  return {}
}

export function normalizeDisplayGroupBookingConfig(
  config: Partial<DisplayGroupBookingPageConfig> | null | undefined
): DisplayGroupBookingPageConfig {
  return {
    priceDisplay:
      config?.priceDisplay === 'from' || config?.priceDisplay === 'hidden'
        ? config.priceDisplay
        : DEFAULT_DISPLAY_GROUP_BOOKING_CONFIG.priceDisplay,
  }
}

export function normalizeServiceBookingConfig(
  config: Partial<ServiceBookingPageConfig> | null | undefined
): ServiceBookingPageConfig {
  return {
    badgeLabel: normalizeText(config?.badgeLabel).slice(0, 32),
    priceDisplay:
      config?.priceDisplay === 'standard' ||
      config?.priceDisplay === 'from' ||
      config?.priceDisplay === 'hidden'
        ? config.priceDisplay
        : DEFAULT_SERVICE_BOOKING_CONFIG.priceDisplay,
    prepChecklist: Array.isArray(config?.prepChecklist)
      ? config.prepChecklist.map(normalizeText).filter(Boolean).slice(0, 8)
      : [],
    imageUrl: normalizeText(config?.imageUrl).slice(0, 2048),
    includedItems: normalizeStringList(config?.includedItems, 8, 90),
    featuredLabel: normalizeText(config?.featuredLabel).slice(0, 32),
    upsellNote: normalizeText(config?.upsellNote).slice(0, 160),
  }
}

export function formatBookingPrice(price: number | null, priceDisplay: ServicePriceDisplay) {
  if (price == null || priceDisplay === 'hidden') return null

  const formatted = `$${(price / 100).toFixed(2)}`
  return priceDisplay === 'from' ? `From ${formatted}` : formatted
}

export function resolveServicePriceDisplay(
  serviceConfig: ServiceBookingPageConfig,
  displayGroupConfig: DisplayGroupBookingPageConfig | null | undefined
): ServicePriceDisplay {
  return serviceConfig.priceDisplay === 'inherit'
    ? normalizeDisplayGroupBookingConfig(displayGroupConfig).priceDisplay
    : serviceConfig.priceDisplay
}

export function getBookingPolicyItems(theme: OperatorBookingPageTheme) {
  const items = [
    ['Access', theme.bookingCopy.accessInstructions],
    ['Weather', theme.bookingCopy.weatherPolicy],
    ['Cancellation', theme.bookingCopy.cancellationPolicy],
  ] as const

  return items.filter(([, value]) => value.length > 0).map(([label, value]) => ({ label, value }))
}

export function getConfirmationContent(
  theme: OperatorBookingPageTheme,
  context: ConfirmationTemplateContext = {}
) {
  return {
    headline: renderConfirmationTemplate(theme.confirmation.headline || "You're booked.", context),
    body: renderConfirmationTemplate(theme.confirmation.body, context),
  }
}

export function renderConfirmationTemplate(template: string, context: ConfirmationTemplateContext) {
  if (!template) return ''

  return template.replace(/\{\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*\}\}/g, (match, token: string) => {
    if (!isConfirmationTemplateToken(token)) return match

    const value = context[token]
    return value == null || value === '' ? match : value
  })
}

function normalizeText(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeStringList(value: unknown, maxItems: number, maxLength: number) {
  return Array.isArray(value)
    ? value
        .map(normalizeText)
        .filter(Boolean)
        .map((item) => item.slice(0, maxLength))
        .slice(0, maxItems)
    : []
}

function normalizeCompletionCta(
  value: unknown,
  fallback: OperatorBookingPageTheme['completion']['primaryCta']
) {
  return value === 'manage' || value === 'rebook' || value === 'contact' || value === 'none'
    ? value
    : fallback
}

function normalizeSurfaceStyle(value: unknown): OperatorBookingPageTheme['surfaceStyle'] {
  if (value === 'modern' || value === 'industrial' || value === 'editorial') return value
  if (value === 'clean') return 'modern'
  if (value === 'bold') return 'industrial'
  return DEFAULT_OPERATOR_BOOKING_THEME.surfaceStyle
}

function normalizeFlowStyle(value: unknown): OperatorBookingPageTheme['flowStyle'] {
  if (value === 'menu_first' || value === 'address_first') return value
  return DEFAULT_OPERATOR_BOOKING_THEME.flowStyle
}

function normalizePageLayout(value: unknown): OperatorBookingPageTheme['pageLayout'] {
  if (value === 'standard' || value === 'showcase' || value === 'split') return value
  return DEFAULT_OPERATOR_BOOKING_THEME.pageLayout
}

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)
}

function isConfirmationTemplateToken(
  value: string
): value is (typeof CONFIRMATION_TEMPLATE_TOKENS)[number] {
  return CONFIRMATION_TEMPLATE_TOKENS.includes(
    value as (typeof CONFIRMATION_TEMPLATE_TOKENS)[number]
  )
}
